import { useEffect, useState } from "react";

const REPEAT_OPTIONS = ["daily", "weekly", "monthly", "yearly"];
const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const Pill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 h-8 rounded-full text-[13px] border transition-colors ${active
      ? "!border-[#2563eb] text-[#1E1E1E] bg-[#F4F5FF]"
      : "!border-[#F5F5F5] text-[#475569] !bg-[#F5F5F5]"
      }`}
  >
    {children}
  </button>
);

const DayPill = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 h-9 rounded-full text-[13px] border transition-colors ${active
      ? "!bg-[#F4F5FF] !text-[#1E1E1E] !border-[#5C6CFF]"
      : "!bg-[#F4F5FF] !text-[#1E1E1E] !border-none "
      }`}
  >
    {children}
  </button>
);

const NumberInput = ({ value, onChange, min = 1 }) => (
  <input
    type="number"
    min={min}
    value={value}
    onChange={(event) => onChange(Number(event.target.value) || min)}
    className="w-16 h-9 border !border-[#D9D9D9] rounded-md px-2 text-[14px] outline-none "
  />
);

const TimeInput = ({ value, onChange }) => (
  <input
    type="time"
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="w-36 h-9 border !border-[#D9D9D9] rounded-md px-2 text-[14px] outline-none focus:border-[#94a3b8]"
  />
);

const CreateScheduleModal = ({
  open,
  initial,
  onClose,
  onCreate,
}) => {
  const [repeat, setRepeat] = useState("daily");
  const [weekDays, setWeekDays] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [runCount, setRunCount] = useState(1);
  const [time, setTime] = useState("09:00");

  useEffect(() => {
    if (!open) return;
    setRepeat(REPEAT_OPTIONS.includes(initial?.repeat) ? initial.repeat : "daily");
    setWeekDays(
      Array.isArray(initial?.weekDays) && initial.weekDays.length
        ? initial.weekDays
        : ["Monday", "Tuesday", "Wednesday", "Thursday"],
    );
    setScheduleDate(initial?.scheduleDate || "");
    setRunCount(initial?.runCount || 1);
    setTime(initial?.time || "09:00");
  }, [open, initial]);

  if (!open) return null;

  const toggleWeekDay = (dayFull) => {
    setWeekDays((current) =>
      current.includes(dayFull)
        ? current.filter((existing) => existing !== dayFull)
        : [...current, dayFull],
    );
  };

  const handleCreate = () => {
    onCreate?.({
      repeat,
      weekDays,
      scheduleDate,
      runCount,
      time,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[480px] max-w-[92vw] p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-[16px] font-semibold text-[#0f172a]">
            Create A Schedule
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-[#94a3b8] hover:text-black"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Repeat */}
        <div className="mt-4">
          <div className="text-[13px] text-[#64748b] mb-2">Repeat</div>
          <div className="flex items-center gap-2">
            {REPEAT_OPTIONS.map((option) => (
              <Pill
                key={option}
                active={repeat === option}
                onClick={() => setRepeat(option)}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </Pill>
            ))}
          </div>
        </div>

        {/* Weekly: Runs X Weeks + Runs on day pills */}
        {repeat === "weekly" && (
          <>
            <div className="mt-5">
              <div className="text-[13px] text-[#757575] mb-2">Runs</div>
              <div className="flex items-center gap-2">
                <NumberInput value={runCount} onChange={setRunCount} />
                <span className="text-[13px] text-[#757575]">Weeks</span>
              </div>
            </div>
            <div className="mt-5">
              <div className="text-[13px] text-[#757575] mb-2">Runs on</div>
              <div className="flex items-center gap-2 flex-wrap">
                {SHORT_DAYS.map((short, index) => {
                  const fullName = FULL_DAYS[index];
                  return (
                    <DayPill
                      key={short}
                      active={weekDays.includes(fullName)}
                      onClick={() => toggleWeekDay(fullName)}
                    >
                      {short}
                    </DayPill>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Monthly: Runs X months + Run count + Date picker */}
        {repeat === "monthly" && (
          <>

            <div className="mt-5">
              <div className="text-[13px] text-[#64748b] mb-2">Runs</div>
              <div className="flex items-center gap-2">
                <NumberInput value={runCount} onChange={setRunCount} />
                <span className="text-[13px] text-[#64748b]">Months</span>
              </div>
            </div>
            <div className="mt-5">
              <div className="text-[13px] text-[#64748b] mb-2">Date</div>
              <input
                type="date"
                value={scheduleDate}
                onChange={(event) => setScheduleDate(event.target.value)}
                className="h-9 border !border-[#D9D9D9] rounded-md px-2 text-[14px] outline-none focus:border-[#94a3b8]"
              />
            </div>
          </>
        )}

        {/* Yearly: Runs X Year(s) + Date picker */}
        {repeat === "yearly" && (
          <>
            <div className="mt-5">
              <div className="text-[13px] text-[#64748b] mb-2">Runs</div>
              <div className="flex items-center gap-2">
                <NumberInput value={runCount} onChange={setRunCount} />
                <span className="text-[13px] text-[#64748b]">Year(s)</span>
              </div>
            </div>
            <div className="mt-5">
              <div className="text-[13px] text-[#64748b] mb-2">Date</div>
              <input
                type="date"
                value={scheduleDate}
                onChange={(event) => setScheduleDate(event.target.value)}
                className="h-9 border !border-[#D9D9D9] rounded-md px-2 text-[14px] outline-none focus:border-[#94a3b8]"
              />
            </div>
          </>
        )}

        {/* Time (always) */}
        <div className="mt-5">
          <div className="text-[13px] text-[#64748b] mb-2">Time</div>
          <TimeInput value={time} onChange={setTime} />
        </div>

        {/* Create button */}
        <button
          type="button"
          onClick={handleCreate}
          className="mt-6 w-full h-11 rounded-md bg-[#1E1E1E]  text-white font-medium"
        >
          Create Schedule
        </button>
      </div>
    </div>
  );
};

export default CreateScheduleModal;
